import React, { useState } from 'react';
import { XMarkIcon, FlagIcon } from '@heroicons/react/24/outline';
import Button from './Button';
import { reportsService } from '../../services/reportsService';

interface ReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  targetType: 'POST' | 'COMMENT' | 'USER';
  targetId: string;
  targetTitle?: string; // For display purposes
  onReportSubmitted?: () => void;
}

const REPORT_REASONS = {
  POST: [
    { value: 'SPAM', label: 'Spam or misleading' },
    { value: 'HARASSMENT', label: 'Harassment or bullying' },
    { value: 'HATE_SPEECH', label: 'Hate speech or discrimination' },
    { value: 'VIOLENCE', label: 'Violence or threats' },
    { value: 'INAPPROPRIATE', label: 'Inappropriate content' },
    { value: 'COPYRIGHT', label: 'Copyright infringement' },
    { value: 'OTHER', label: 'Other' }
  ],
  COMMENT: [
    { value: 'SPAM', label: 'Spam or misleading' },
    { value: 'HARASSMENT', label: 'Harassment or bullying' },
    { value: 'HATE_SPEECH', label: 'Hate speech or discrimination' },
    { value: 'VIOLENCE', label: 'Violence or threats' },
    { value: 'INAPPROPRIATE', label: 'Inappropriate content' },
    { value: 'OTHER', label: 'Other' }
  ],
  USER: [
    { value: 'HARASSMENT', label: 'Harassment or bullying' },
    { value: 'HATE_SPEECH', label: 'Hate speech or discrimination' },
    { value: 'VIOLENCE', label: 'Violence or threats' },
    { value: 'SPAM', label: 'Spam or unwanted contact' },
    { value: 'IMPERSONATION', label: 'Impersonation' },
    { value: 'OTHER', label: 'Other' }
  ]
};

const ReportModal: React.FC<ReportModalProps> = ({
  isOpen,
  onClose,
  targetType,
  targetId,
  targetTitle,
  onReportSubmitted
}) => {
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reasons = REPORT_REASONS[targetType];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason) return;

    setSubmitting(true);
    setError(null);

    try {
      await reportsService.submitReport({
        targetType,
        targetId,
        reason,
        details: details.trim() || undefined
      });

      setSubmitted(true);
      if (onReportSubmitted) {
        onReportSubmitted();
      }

      // Close modal after a short delay
      setTimeout(() => {
        onClose();
        resetForm();
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setReason('');
    setDetails('');
    setSubmitted(false);
    setError(null);
  };

  const handleClose = () => {
    onClose();
    resetForm();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <FlagIcon className="w-6 h-6 text-red-500 mr-3" />
            <h2 className="text-lg font-semibold text-gray-900">Report {targetType.toLowerCase()}</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Report Submitted</h3>
              <p className="text-gray-600">
                Thank you for helping keep our community safe. Our team will review this report.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {targetTitle && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">
                    Reporting: <span className="font-medium text-gray-900">{targetTitle}</span>
                  </p>
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for report *
                </label>
                <select
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
                  required
                >
                  <option value="">Select a reason</option>
                  {reasons.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional details (optional)
                </label>
                <textarea
                  value={details}
                  onChange={(e) => setDetails(e.target.value)}
                  placeholder="Provide more context about why you're reporting this..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500 resize-none"
                  rows={3}
                  maxLength={2000}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {details.length}/2000 characters
                </p>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleClose}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!reason || submitting}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {submitting ? 'Submitting...' : 'Submit Report'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportModal;